<template>
  <div
    v-if="isShownUpcomingApplicableOrders"
    data-testid="upcoming-three-applicable-orders"
  >
    <h3
      data-testid="title"
      class="text-size-14 text-weight-500 text-primary-dark section-title"
      :class="titleClass"
    >
      {{ title }}
    </h3>
    <div class="date-list" data-testid="order-list">
      <SimpleOrderListItem
        :order="order"
        :key="order.id"
        v-for="order in orders"
      />
    </div>
    <RouterLink
      class="read-more"
      :to="readMoreLink"
      v-if="isShownReadMore"
      data-testid="read-more"
    >
      <span class="read-more-text" data-testid="read-more-text">{{
        `お仕事一覧を見る (全${applicableOrderCount}件)`
      }}</span>
      <FeatherIcon icon="chevron-right" size="24" />
    </RouterLink>
  </div>
</template>

<script lang="ts">
import { Component, Prop, Vue } from "vue-facing-decorator";
import { Order, SlimOrder } from "@/types/order/Order";
import FeatherIcon from "@/components/atoms/other/FeatherIcon.vue";
import SimpleOrderListItem from "@/components/molecules/orders/SimpleOrderListItem.vue";

const DISPLAY_DAYS = 3;

@Component({
  components: {
    FeatherIcon,
    SimpleOrderListItem
  }
})
export default class RecentOrders extends Vue {
  @Prop({
    type: String,
    default: "同じ事業所の募集中のお仕事一覧"
  })
  title!: string;

  @Prop({
    type: String,
    default: ""
  })
  titleClass!: string;

  // 現在参照されている案件
  @Prop({
    type: Object,
    required: true
  })
  currentOrder!: Order;

  // 直近で応募可能な案件のリスト
  @Prop({
    type: Array,
    default: () => []
  })
  orders!: SlimOrder[];

  // 直近応募可能な案件の数
  @Prop({
    type: Number,
    required: true,
    default: 0
  })
  applicableOrderCount!: number;

  get isShownReadMore() {
    return this.applicableOrderCount > DISPLAY_DAYS;
  }

  /**
   * 他の案件も見るボタンをクリックしたときの処理
   */
  get readMoreLink() {
    return {
      name: "SearchByOffice",
      params: {
        officeId: `${this.currentOrder.office.id}`
      },
      hash: "#orderList"
    };
  }

  /**
   * 申込可能リストの表示可否
   * 申込可能な案件が0件の場合は表示しない
   */
  get isShownUpcomingApplicableOrders() {
    return this.orders.length > 0;
  }

  /**
   * 案件をクリックしたときの処理
   */
  getDetailRoute(order: SlimOrder) {
    return {
      name: "OrdersDetail",
      params: {
        orderId: `${order.id}`
      }
    };
  }
}
</script>

<style lang="scss" scoped>
.section-title {
  margin-bottom: 10px;
}

.date-list {
  display: flex;
  flex-direction: column;
  gap: 6px;
}

.order-item {
  display: flex;
  align-items: center;
  cursor: pointer;
}

.date-square {
  display: flex;
  flex-direction: column;
  min-width: 15vw;
  padding: 4px;
  margin-right: 8px;
  text-align: center;
  background: $white;
  border: 1px solid $gray-03;
  border-radius: 8px;
}

.order-detail {
  display: flex;
  flex-direction: column;
  justify-content: center;
  margin-left: 10px;
}

.allowance {
  display: flex;
  gap: 5px;
}

.office-hour {
  display: flex;
  align-items: center;
}

.salary {
  font-weight: 600;
  color: $red;
}

.date {
  margin-bottom: 4px;
  font-weight: 600;
}

.weekday {
  width: 100%;
  padding-bottom: 8px;
  font-size: 12px;
  line-height: 1;
  text-align: center;

  &.sun {
    color: $red;
  }

  &.sat {
    color: $blue;
  }
}

.read-more {
  display: flex;
  justify-content: center;
  padding: 10px 0;
  margin-top: 20px;
  cursor: pointer;
  border: 1px solid $gray-03;
  border-radius: 8px;
}

.read-more-text {
  font-weight: 500;
}

.chevron-icon {
  margin-left: auto;
}
</style>
